import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { Study, StudyParticipant } from '../../types/study.types';

interface StudyAnalyticsProps {
  study: Study;
  participants: StudyParticipant[];
}

const COLORS = ['#0d9488', '#3b82f6', '#eab308', '#ef4444', '#8b5cf6'];
const STATUS_COLORS: Record<string, string> = {
  enrolled: '#3b82f6',
  active: '#22c55e',
  completed: '#0d9488',
  withdrawn: '#ef4444',
  lost_to_followup: '#eab308',
};

const StudyAnalytics: React.FC<StudyAnalyticsProps> = ({ study, participants }) => {
  // Status distribution
  const statusData = [
    { name: 'Enrolled', value: participants.filter((p) => p.status === 'enrolled').length },
    { name: 'Active', value: participants.filter((p) => p.status === 'active').length },
    { name: 'Completed', value: participants.filter((p) => p.status === 'completed').length },
    { name: 'Withdrawn', value: participants.filter((p) => p.status === 'withdrawn').length },
    { name: 'Lost', value: participants.filter((p) => p.status === 'lost_to_followup').length },
  ].filter((d) => d.value > 0);

  // Survey completion by timepoint
  const timepointCompletionData = study.surveySchedule?.map((tp) => {
    const relevantParticipants = participants.filter(
      (p) => p.status !== 'withdrawn' && p.status !== 'lost_to_followup'
    );
    const completed = relevantParticipants.filter((p) =>
      p.surveyProgress && p.surveyProgress[tp.id]?.status === 'completed'
    ).length;
    const pending = relevantParticipants.filter((p) =>
      p.surveyProgress && (p.surveyProgress[tp.id]?.status === 'pending' || p.surveyProgress[tp.id]?.status === 'sent')
    ).length;
    const missed = relevantParticipants.filter((p) =>
      p.surveyProgress && p.surveyProgress[tp.id]?.status === 'missed'
    ).length;

    return {
      name: tp.name,
      completed,
      pending,
      missed,
    };
  }) || [];

  // Enrollment over time (by week)
  const enrollmentOverTime = React.useMemo(() => {
    if (participants.length === 0) return [];

    const sortedParticipants = [...participants].sort((a, b) => {
      const dateA = a.enrolledAt && 'toDate' in a.enrolledAt ? a.enrolledAt.toDate() : new Date();
      const dateB = b.enrolledAt && 'toDate' in b.enrolledAt ? b.enrolledAt.toDate() : new Date();
      return dateA.getTime() - dateB.getTime();
    });

    const weeklyData: Record<string, number> = {};
    let cumulativeCount = 0;

    sortedParticipants.forEach((p) => {
      if (!p.enrolledAt) return;
      const date = p.enrolledAt && 'toDate' in p.enrolledAt ? p.enrolledAt.toDate() : new Date();
      const weekStart = new Date(date);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];

      cumulativeCount++;
      weeklyData[weekKey] = cumulativeCount;
    });

    return Object.entries(weeklyData).map(([week, count]) => ({
      week: new Date(week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      participants: count,
    }));
  }, [participants]);

  // Overall completion rate
  const overallCompletionRate = React.useMemo(() => {
    const activeParticipants = participants.filter(
      (p) => p.status !== 'withdrawn' && p.status !== 'lost_to_followup'
    );
    if (activeParticipants.length === 0 || !study.surveySchedule?.length) return 0;

    let totalCompleted = 0;
    let totalExpected = 0;

    activeParticipants.forEach((p) => {
      if (p.surveyProgress) {
        const progressEntries = Object.values(p.surveyProgress);
        totalCompleted += progressEntries.filter((sp) => sp.status === 'completed').length;
        totalExpected += progressEntries.length;
      }
    });

    return totalExpected > 0 ? Math.round((totalCompleted / totalExpected) * 100) : 0;
  }, [participants, study.surveySchedule]);

  // Retention rate
  const retentionRate = React.useMemo(() => {
    if (participants.length === 0) return 100;
    const retained = participants.filter(
      (p) => p.status !== 'withdrawn' && p.status !== 'lost_to_followup'
    ).length;
    return Math.round((retained / participants.length) * 100);
  }, [participants]);

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Enrollment Progress</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {study.analytics?.totalEnrolled || 0} / {study.enrollmentSettings?.targetSampleSize || 0}
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div
              className="bg-teal-600 h-2 rounded-full"
              style={{
                width: `${Math.min(
                  ((study.analytics?.totalEnrolled || 0) / (study.enrollmentSettings?.targetSampleSize || 1)) * 100,
                  100
                )}%`,
              }}
            ></div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Retention Rate</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{retentionRate}%</p>
          <p className="text-xs text-gray-500 mt-1">
            {participants.filter((p) => p.status !== 'withdrawn' && p.status !== 'lost_to_followup').length} of {participants.length} retained
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Survey Completion</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{overallCompletionRate}%</p>
          <p className="text-xs text-gray-500 mt-1">Overall completion rate</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Timepoints</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{study.surveySchedule?.length || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Survey timepoints</p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Participant Status</h3>
          {statusData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={(props: any) => `${props.name} ${(props.percent * 100).toFixed(0)}%`}
                  >
                    {statusData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={STATUS_COLORS[entry.name.toLowerCase().replace(' ', '_')] || COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No participants yet
            </div>
          )}
        </div>

        {/* Enrollment Over Time */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Enrollment Over Time</h3>
          {enrollmentOverTime.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={enrollmentOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="participants"
                    stroke="#0d9488"
                    strokeWidth={2}
                    dot={{ fill: '#0d9488' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No enrollment data yet
            </div>
          )}
        </div>
      </div>

      {/* Survey Completion by Timepoint */}
      {timepointCompletionData.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Survey Completion by Timepoint</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timepointCompletionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="completed" stackId="a" fill="#0d9488" name="Completed" />
                <Bar dataKey="pending" stackId="a" fill="#3b82f6" name="Pending" />
                <Bar dataKey="missed" stackId="a" fill="#ef4444" name="Missed" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Detailed Stats Table */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Statistics</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Metric
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Value
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-2.5 text-sm text-gray-900">Total Enrolled</td>
                <td className="px-4 py-2.5 text-sm text-right font-medium">{participants.length}</td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 text-sm text-gray-900">Currently Active</td>
                <td className="px-4 py-2.5 text-sm text-right font-medium">
                  {participants.filter((p) => p.status === 'active' || p.status === 'enrolled').length}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 text-sm text-gray-900">Study Completed</td>
                <td className="px-4 py-2.5 text-sm text-right font-medium">
                  {participants.filter((p) => p.status === 'completed').length}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 text-sm text-gray-900">Withdrawn</td>
                <td className="px-4 py-2.5 text-sm text-right font-medium text-red-600">
                  {participants.filter((p) => p.status === 'withdrawn').length}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 text-sm text-gray-900">Lost to Follow-up</td>
                <td className="px-4 py-2.5 text-sm text-right font-medium text-yellow-600">
                  {participants.filter((p) => p.status === 'lost_to_followup').length}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 text-sm text-gray-900">Enrollment Rate</td>
                <td className="px-4 py-2.5 text-sm text-right font-medium">
                  {Math.round(((study.analytics?.totalEnrolled || 0) / (study.enrollmentSettings?.targetSampleSize || 1)) * 100)}%
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 text-sm text-gray-900">Dropout Rate</td>
                <td className="px-4 py-2.5 text-sm text-right font-medium">
                  {participants.length > 0
                    ? Math.round(
                        ((participants.filter((p) => p.status === 'withdrawn' || p.status === 'lost_to_followup').length) /
                          participants.length) *
                          100
                      )
                    : 0}
                  %
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StudyAnalytics;
